import React, { useEffect } from "react";
import { X } from "lucide-react";

const join = (...classes) => classes.filter(Boolean).join(" ");

/** Contenedor estándar: pantalla completa en móvil, centrado en escritorio. z-[70] > barra inferior. */
export function AppModalShell({
  open,
  onClose,
  title,
  children,
  footer,
  headerAction,
  widthClass = "max-w-2xl",
  zClass = "z-[70]",
  /** En móvil, altura según contenido (evita espacio vacío en modales con poco contenido). */
  fitContent = false,
  /** Si es false, el contenido ocupa el alto del modal y debe scrollear por dentro. */
  bodyScroll = true,
}) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={join(
        "fixed inset-0 flex flex-col",
        fitContent
          ? "justify-end md:items-center md:justify-center md:p-4"
          : "md:items-center md:justify-center md:p-4",
        "bg-slate-950/55 backdrop-blur-sm overlay-enter",
        zClass,
      )}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={join(
          "modal-enter flex flex-col bg-white shadow-2xl w-full min-h-0 mx-auto overflow-hidden",
          fitContent
            ? "max-h-[100dvh] rounded-t-2xl border border-gray-200 border-b-0 md:rounded-xl md:border md:max-h-[min(90vh,900px)]"
            : "h-[100dvh] max-h-[100dvh] md:h-[min(90vh,900px)] md:max-h-[min(90vh,900px)] md:rounded-xl md:border md:border-gray-200",
          widthClass,
        )}
      >
        <div
          className={join(
            "flex items-start justify-between gap-2 shrink-0",
            "px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-100",
            "bg-gradient-to-r from-slate-50 to-white",
            "pt-[max(0.75rem,env(safe-area-inset-top))]",
          )}
        >
          {title ? (
            <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-snug min-w-0 flex-1 pr-1">
              {title}
            </h3>
          ) : (
            <span className="flex-1" />
          )}
          <div className="flex items-center gap-0.5 shrink-0">
            {headerAction}
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="p-2 -mr-1 text-gray-500 hover:text-gray-800 rounded-lg hover:bg-gray-100"
                aria-label="Cerrar"
              >
                <X size={22} />
              </button>
            )}
          </div>
        </div>

        <div
          className={join(
            "reveal-host flex-1 min-h-0 px-4 py-4 sm:px-5 sm:py-5",
            bodyScroll
              ? "overflow-y-auto overscroll-contain"
              : "flex flex-col overflow-hidden",
          )}
        >
          {children}
        </div>

        {footer != null && (
          <div
            className={join(
              "shrink-0 px-4 py-3 sm:px-5 sm:py-4",
              "bg-gray-50 border-t border-gray-100",
              "pb-[max(0.75rem,env(safe-area-inset-bottom))]",
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Pie de modal con botones apilados en móvil. */
export function AppModalFooter({ children, className = "" }) {
  return (
    <div
      className={join(
        "flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-between sm:items-center sm:gap-3",
        className,
      )}
    >
      {children}
    </div>
  );
}

export const AppCard = ({ children, className = "", ...props }) => (
  <div
    className={join(
      "bg-white rounded-xl border border-gray-200 shadow-sm",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

export const AppButton = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  disabled = false,
  type = "button",
  ...props
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed";
  const variants = {
    primary: "bg-indigo-600 text-white hover:bg-indigo-700",
    secondary: "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
    outline: "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50",
    danger: "bg-red-50 text-red-700 border border-red-200 hover:bg-red-100",
    ghost: "text-gray-500 hover:text-indigo-600 hover:bg-indigo-50",
  };
  const sizes = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-sm",
    icon: "h-9 w-9 p-0",
  };

  return (
    <button
      type={type}
      className={join(base, variants[variant], sizes[size], className)}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

export const AppInput = ({ label, className = "", ...props }) => (
  <div className="mb-3">
    {label && (
      <label className="block text-sm font-medium text-gray-600 mb-1.5">
        {label}
      </label>
    )}
    <input
      className={join(
        "w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none text-sm",
        className,
      )}
      {...props}
    />
  </div>
);

export const AppBadge = ({ children, type = "info", className = "" }) => {
  const styles = {
    info: "bg-indigo-50 text-indigo-700 border border-indigo-200",
    success: "bg-emerald-50 text-emerald-700 border border-emerald-200",
    warning: "bg-amber-50 text-amber-700 border border-amber-200",
    danger: "bg-red-50 text-red-700 border border-red-200",
  };
  return (
    <span
      className={join(
        "px-2.5 py-1 rounded-full text-xs font-semibold",
        styles[type] || styles.info,
        className,
      )}
    >
      {children}
    </span>
  );
};

export const AppPageHeader = ({ title, subtitle, actions }) => (
  <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-1">
    <div className="min-w-0">
      <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">{title}</h2>
      {subtitle && <p className="text-xs sm:text-sm text-gray-500">{subtitle}</p>}
    </div>
    {actions ? (
      <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>
    ) : null}
  </div>
);

export const AppModal = ({
  open,
  title,
  onClose,
  children,
  footer,
  widthClass = "max-w-2xl",
}) => (
  <AppModalShell
    open={open}
    onClose={onClose}
    title={title}
    footer={footer}
    widthClass={widthClass}
  >
    {children}
  </AppModalShell>
);

export const AppTable = ({ columns, children, className = "" }) => (
  <div className={join("bg-white rounded-xl border border-gray-200 overflow-hidden", className)}>
    <div className="overflow-x-auto">
      <table className="w-full min-w-[36rem] text-left app-table">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={join(column.align === "right" ? "text-right" : "", column.align === "center" ? "text-center" : "")}
              >
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 reveal-rows">{children}</tbody>
      </table>
    </div>
  </div>
);
